<template>
  <div>
    <!-- HEADER -->
    <div v-if="!isAutoPrint" class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("invoice_print") }}</h4>
        </div>
      </div>

      <div class="page-btn">
        <router-link to="/" class="btn btn-added">
          <span>Back</span>
        </router-link>
      </div>
    </div>

    <!-- CARD -->
    <div class="card table-list-card ip-card" :class="{ 'ip-card-autoprint': isAutoPrint }">
      <form>
        <div v-if="!isAutoPrint" class="ip-search">
          <label class="ip-label">{{ $t('select_voucher') }}</label>
          <div class="ip-search-field">
            <input
              type="text"
              v-model="q"
              @input="searchVoucher"
              class="form-control ip-input"
              :placeholder="$t('search_voucher_by_id')"
              @keydown="handleKeysVoucher"
            />
            <button type="button" class="btn btn-light border ip-clear" @click="clearSearch">
              Clear
            </button>
            <ul v-if="voucher_result.length" class="ip-results">
              <li
                v-for="(voucher, index) in voucher_result"
                :key="voucher.voucher_id"
                :class="{ 'is-active': index === itemIndex }"
                @click="selectVoucher(voucher)"
              >
                {{ voucher.voucher_id }}
              </li>
            </ul>
          </div>
        </div>

        <div class="ip-stage">

        <!-- invoiceStyle: 3 = 80mm Excluding VAT, 4 = 80mm Including VAT. -->
        <div
          id="invoice"
          class="hm-paper hm-paper-thermal"
          v-if="isSlip && results.length"
        >
          <div class="hm-inv hm-inv-thermal">
            <div v-if="company.logo" class="th-center">
              <img :src="company.logo" class="co-logo th-logo" alt="">
            </div>
            <div class="th-center th-company">
              <div class="th-strong co-name">{{ company.name }}</div>
              <div v-if="company.address">{{ company.address }}</div>
              <div v-if="company.phone">Phone: {{ company.phone }}</div>
              <div v-if="company.email">Email: {{ company.email }}</div>
              <div v-if="company.tin">TIN: {{ company.tin }}</div>
              <div v-if="company.bin">BIN: {{ company.bin }}</div>
            </div>
            <div v-if="company.header_message" class="th-center th-strong th-banner">{{ company.header_message }}</div>
            <div class="th-dash"></div>

            <div class="th-center th-strong">INVOICE</div>
            <div class="th-pair"><span>Invoice No:</span><span>{{ q }}</span></div>
            <div class="th-pair"><span>Date:</span><span>{{ new Date().toLocaleString() }}</span></div>
            <div class="th-pair"><span>User:</span><span>{{ invoicePayload.user }}</span></div>

            <div class="th-dash"></div>
            <div class="th-pair"><span>To:</span><span>{{ invoicePayload.customer.name }}</span></div>
            <div class="th-pair"><span>Contact:</span><span>{{ invoicePayload.customer.phone }}</span></div>
            <div class="th-pair"><span>Address:</span><span>{{ invoicePayload.customer.address }}</span></div>

            <div class="th-dash"></div>
            <div class="th-item" v-for="(item, index) in results" :key="index">
              <div>{{ index + 1 }}. {{ invoiceProductName(item) }}</div>
              <div
                v-if="item.is_warranty && item.warranty_period_days"
                style="font-size:0.72rem;"
              >
                Warranty: {{ item.warranty_period_days }} days
                ({{ item.warranty_start || '-' }} to {{ item.warranty_end || '-' }})
              </div>
              <div class="th-pair">
                <span>{{ item.unit ? `${item.qty} ${item.unit}` : item.qty }} × {{ item.unit_price }}</span>
                <span>{{ item.total_price }}</span>
              </div>
            </div>

            <div class="th-dash"></div>
            <div class="th-pair"><span>Total Amount</span><span>{{ formatMoney(summary.sub_total) }}</span></div>
            <div class="th-pair"><span>{{ discountLabel }}</span><span>{{ formatMoney(summary.discount) }}</span></div>
            <div v-if="invoiceStyle === 4" class="th-pair">
              <span>{{ vatLabel }}</span><span>{{ formatMoney(summary.vat) }}</span>
            </div>
            <div class="th-pair"><span>Net Payable</span><span>{{ formatMoney(summary.total) }}</span></div>
            <div class="th-pair"><span>Rounded Payable</span><span>{{ formatMoney(summary.rounded_payable ?? summary.total) }}</span></div>
            <div class="th-pair"><span>Paid Amount</span><span>{{ formatMoney(summary.received) }}</span></div>
            <div class="th-pair"><span>Change</span><span>{{ formatMoney(summary.change ?? 0) }}</span></div>
            <div class="th-pair"><span>Due</span><span>{{ formatMoney(summary.due ?? 0) }}</span></div>
            <div class="th-dash"></div>
            <div class="th-pair th-strong"><span>Grand Total</span><span>{{ formatMoney(summary.rounded_payable ?? summary.total) }}</span></div>
            <div class="th-word"><b>In Word:</b> {{ amountInWords }}</div>
            <div v-if="company.footer_message" class="th-center th-banner">{{ company.footer_message }}</div>
            <div v-if="showInvoiceBarcode" class="th-center th-invoice-barcode">
              <img :src="invoiceBarcodeSrc" alt="Invoice barcode" />
            </div>
            <div class="th-center th-powered">Powered by otobonik | www.otobonik.com | 01816293649</div>
          </div>
        </div>

        <!-- invoiceStyle: 1 = A4, 2 = A5, 5 = A4 Warranty. -->
        <div
          v-if="isPageInvoice && results.length"
          :id="invoicePaperId"
          class="hm-paper"
          :class="{ 'hm-paper-a5': invoiceStyle === 2, 'hm-paper-warranty': isWarrantyInvoice }"
        >
          <div class="hm-inv hm-inv-a4">
            <div class="a4-head">
              <div class="a4-top">
                <div class="co-block">
                  <img v-if="company.logo" :src="company.logo" class="co-logo" alt="">
                  <div>
                    <div class="co-name">{{ company.name }}</div>
                    <div v-if="company.address" class="co-line">{{ company.address }}</div>
                    <div v-if="company.phone" class="co-line">Phone: {{ company.phone }}</div>
                    <div v-if="company.email" class="co-line">Email: {{ company.email }}</div>
                    <div v-if="company.tin" class="co-line">TIN: {{ company.tin }}</div>
                    <div v-if="company.bin" class="co-line">BIN: {{ company.bin }}</div>
                  </div>
                </div>
                <div class="a4-meta">
                  <div class="inv-title">{{ isWarrantyInvoice ? 'WARRANTY INVOICE' : 'INVOICE' }}</div>
                  <div>Invoice No: {{ q }}</div>
                  <div>Date: {{ new Date().toLocaleString() }}</div>
                  <div>User: {{ invoicePayload.user }}</div>
                </div>
              </div>
              <div v-if="company.header_message" class="inv-banner">{{ company.header_message }}</div>
            </div>

            <div class="bill-to">
              <span>To:</span><span>{{ invoicePayload.customer.name }}</span>
              <span>Contact:</span><span>{{ invoicePayload.customer.phone }}</span>
              <span>Address:</span><span>{{ invoicePayload.customer.address }}</span>
            </div>

            <table class="a4-items" :class="{ 'a4-items-warranty': isWarrantyInvoice }">
              <thead>
                <tr v-if="isWarrantyInvoice">
                  <th class="c">#</th>
                  <th>Product</th>
                  <th>Variant</th>
                  <th>Serial</th>
                  <th class="c">Warranty Days</th>
                  <th class="c">Start</th>
                  <th class="c">End</th>
                  <th class="r">Qty</th>
                  <th class="r">Unit Price</th>
                  <th class="r">Amount</th>
                </tr>
                <tr v-else>
                  <th class="c">#</th>
                  <th>Product Name</th>
                  <th class="r">Qty</th>
                  <th class="r">Unit Price</th>
                  <th class="r">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in results" :key="i">
                  <template v-if="isWarrantyInvoice">
                    <td class="c">{{ i + 1 }}</td>
                    <td>{{ item.item_name }}</td>
                    <td>{{ item.is_warranty ? (item.variant_label || '-') : '-' }}</td>
                    <td>{{ item.is_warranty ? (item.serial_number || '-') : '-' }}</td>
                    <td class="c">{{ item.is_warranty ? (item.warranty_period_days || '-') : '-' }}</td>
                    <td class="c">{{ item.is_warranty ? (item.warranty_start || '-') : '-' }}</td>
                    <td class="c">{{ item.is_warranty ? (item.warranty_end || '-') : '-' }}</td>
                    <td class="r">{{ item.unit ? `${item.qty} ${item.unit}` : item.qty }}</td>
                    <td class="r">{{ item.unit_price }}</td>
                    <td class="r">{{ item.total_price }}</td>
                  </template>
                  <template v-else>
                    <td class="c">{{ i + 1 }}</td>
                    <td>{{ invoiceProductName(item) }}</td>
                    <td class="r">{{ item.unit ? `${item.qty} ${item.unit}` : item.qty }}</td>
                    <td class="r">{{ item.unit_price }}</td>
                    <td class="r">{{ item.total_price }}</td>
                  </template>
                </tr>
              </tbody>
            </table>

            <table class="a4-totals">
              <tbody>
                <tr>
                  <td>Total Amount</td>
                  <td class="r">{{ formatMoney(summary.sub_total) }}</td>
                </tr>
                <tr>
                  <td>Discount</td>
                  <td class="r">{{ formatMoney(summary.discount) }}</td>
                </tr>
                <tr>
                  <td>Net Payable</td>
                  <td class="r">{{ formatMoney(summary.total) }}</td>
                </tr>
                <tr>
                  <td>Rounded Payable</td>
                  <td class="r">{{ formatMoney(summary.rounded_payable ?? summary.total) }}</td>
                </tr>
                <tr>
                  <td>Paid Amount</td>
                  <td class="r">{{ formatMoney(summary.received) }}</td>
                </tr>
                <tr>
                  <td>Change</td>
                  <td class="r">{{ formatMoney(summary.change ?? 0) }}</td>
                </tr>
                <tr>
                  <td>Due</td>
                  <td class="r">{{ formatMoney(summary.due ?? 0) }}</td>
                </tr>
                <tr class="grand">
                  <td>Grand Total</td>
                  <td class="r">{{ formatMoney(summary.rounded_payable ?? summary.total) }}</td>
                </tr>
              </tbody>
            </table>

            <p class="in-word"><b>In Word:</b> {{ amountInWords }}</p>

            <div v-if="invoiceStyle !== 2" class="signs">
              <div><span></span>Received By</div>
              <div><span></span>Checked By</div>
              <div><span></span>Authorized By</div>
            </div>
            <p v-if="company.footer_message" class="inv-footer-msg">{{ company.footer_message }}</p>
            <div v-if="showInvoiceBarcode" class="inv-invoice-barcode">
              <img :src="invoiceBarcodeSrc" alt="Invoice barcode" />
            </div>
            <p class="inv-powered">Powered by otobonik | www.otobonik.com | 01816293649</p>
          </div>
        </div>
        </div>

        <div v-if="!isAutoPrint" class="ip-actions">
          <button type="button" class="btn btn-secondary me-2" @click="reset">
            Cancel
          </button>

          <button type="button" class="btn btn-primary" @click="printPreview">
            Print
          </button>
        </div>
        <div v-else class="ip-autoprint-status text-muted">
          Preparing invoice print…
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiUrls from "@/urls";
import { useToast } from "vue-toastification";

export default {
  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      q: "",
      voucher_result: [],
      selectedVoucher: null,
      results: [],
      summary: {},
      itemIndex: 0,
      invoicePayload: null,
      invoiceStyle: null,
      autoPrintPending: false,
    };
  },

  computed: {
    isAutoPrint() {
      const flag = String(this.$route.query.autoprint || "").toLowerCase();
      return ["1", "true", "yes"].includes(flag);
    },

    company() {
      return this.invoicePayload?.company || {};
    },

    amountInWords() {
      return this.summary.amount_in_words || '';
    },

    isPageInvoice() {
      return this.invoiceStyle === 1 || this.invoiceStyle === 2 || this.invoiceStyle === 5;
    },

    isWarrantyInvoice() {
      return this.invoiceStyle === 5;
    },

    invoicePaperId() {
      if (this.invoiceStyle === 2) return 'invoice-a5';
      if (this.invoiceStyle === 5) return 'invoice-a4-warranty';
      return 'invoice-a4';
    },

    isSlip() {
      return this.invoiceStyle === 3 || this.invoiceStyle === 4;
    },

    discountLabel() {
      if (this.invoiceStyle !== 4 || !(Number(this.summary.discount) > 0)) return "Discount";
      const percent = this.invoicePayload?.discount_percent;
      return percent != null && percent !== "" && Number(percent) !== 0
        ? `Discount (${percent}%)`
        : "Discount";
    },

    vatLabel() {
      if (!(Number(this.summary.vat) > 0)) return "VAT";
      const percent = this.invoicePayload?.vat_percent;
      return percent != null && percent !== "" && Number(percent) !== 0
        ? `VAT (${percent}%)`
        : "VAT";
    },

    invoiceBarcodeSrc() {
      const raw = this.invoicePayload?.invoice_barcode;
      return raw ? `data:image/png;base64,${raw}` : "";
    },

    showInvoiceBarcode() {
      return !!this.company?.show_barcode && !!this.invoiceBarcodeSrc;
    },
  },

  async mounted() {
    const voucherId = this.$route.query.voucher_id;
    if (!voucherId) return;

    await this.selectVoucher({ voucher_id: String(voucherId) });

    if (!this.isAutoPrint) return;
    if (!this.results.length) {
      this.toast.warning("Invoice not found for print");
      return;
    }

    this.autoPrintPending = true;
    await this.$nextTick();
    // Wait for invoice DOM (thermal/A4/A5) to paint, then open the same Print Preview
    // document used by the Print button (same window — avoids Firefox nested-popup block).
    setTimeout(() => {
      this.printPreview({ sameWindow: true });
      this.autoPrintPending = false;
    }, 400);
  },

  methods: {
    formatMoney(amount) {
      return (Number(amount) || 0).toFixed(2);
    },

    clearSearch() {
      this.q = "";
      this.voucher_result = [];
      this.itemIndex = 0;
    },

    async searchVoucher() {
      if (!this.q) {
        this.voucher_result = [];
        this.itemIndex = 0;
        return;
      }

      try {
        const res = await this.$axios.get(apiUrls.invoice_print.get, {
          params: { voucher_id: this.q },
        });

        // If backend gives list
        if (res.data.results) {
          this.voucher_result = res.data.results;
        } else {
          // fallback
          this.voucher_result = [];
        }

        this.itemIndex = 0;

      } catch (e) {
        this.voucher_result = [];
      }
    },

    async fetchInvoice(voucher_id) {
      try {
        const res = await this.$axios.get(apiUrls.invoice_print.get, {
          params: { voucher_id, detail: 1 },
        });

        this.results = res.data.items || [];
        this.summary = res.data.summary || {};
        this.invoiceStyle = res.data.invoice_style != null
          ? Number(res.data.invoice_style)
          : null;

        this.invoicePayload = res.data;

      } catch (e) {
        this.results = [];
        this.summary = {};
        this.invoicePayload = null;
        this.invoiceStyle = null;
      }
    },

    async selectVoucher(voucher) {
      this.selectedVoucher = voucher;
      this.q = voucher.voucher_id;

      this.voucher_result = [];
      this.itemIndex = 0;

      await this.fetchInvoice(voucher.voucher_id);
    },

    handleKeysVoucher(e) {
      if (e.key === "ArrowDown") {
        this.itemIndex = Math.min(this.itemIndex + 1, this.voucher_result.length - 1);
        e.preventDefault();
      } else if (e.key === "ArrowUp") {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        e.preventDefault();
      } else if (e.key === "Enter") {
        if (this.voucher_result[this.itemIndex]) {
          this.selectVoucher(this.voucher_result[this.itemIndex]);
        }
        e.preventDefault();
      }
    },

    collectPrintCss() {
      // Local `vue serve` injects CSS into <style>. Production builds put the
      // same rules in <link rel="stylesheet"> files. Reading CSSOM covers both.
      const chunks = [];
      for (const sheet of Array.from(document.styleSheets)) {
        try {
          chunks.push([...sheet.cssRules].map((rule) => rule.cssText).join("\n"));
        } catch (err) {
          if (sheet.href) chunks.push(`@import url("${sheet.href}");`);
        }
      }
      return chunks.join("\n").replace(/<\/(style|script)/gi, "<\\/$1");
    },

    printMarkup(source) {
      const clone = source.cloneNode(true);
      clone.querySelectorAll("[src]").forEach((el) => {
        const src = el.getAttribute("src");
        if (!src) return;
        try {
          el.setAttribute("src", new URL(src, window.location.href).href);
        } catch (err) { /* keep original src */ }
      });
      return clone.outerHTML;
    },

    invoiceProductName(item) {
      let name = item?.item_name || "";
      if (item?.variant_label) {
        name += ` (${item.variant_label})`;
      }
      if (item?.is_warranty && item?.serial_number && this.invoiceStyle !== 5) {
        name += ` [SN: ${item.serial_number}]`;
      }
      return name;
    },

    printPreview({ closeOpenerOnDone = false, sameWindow = false } = {}) {
      const isA5 = this.invoiceStyle === 2;
      const isA4 = this.invoiceStyle === 1 || this.invoiceStyle === 5;
      const source = document.getElementById(this.invoicePaperId || (isA5 ? "invoice-a5" : isA4 ? "invoice-a4" : "invoice"));
      if (!source) {
        this.toast.warning("Select voucher first");
        return;
      }
      const isThermal = !isA4 && !isA5;
      const pageSize = isA5 ? "A5" : isA4 ? "A4" : `80mm ${Math.max(40, Math.ceil(source.scrollHeight * 25.4 / 96) + 12)}mm`;
      const pageMargin = isA5 ? "8mm 8mm 8mm 8mm" : isA4 ? "18mm 16mm 20mm 16mm" : "0";
      const thermalCss = isThermal ? `
              #invoice, .hm-paper-thermal {
                box-sizing: border-box !important;
                width: 80mm !important;
                max-width: 80mm !important;
                padding: 4mm !important;
              }
              @media screen {
                html, body { width: 80mm; }
              }
              @media print {
                #invoice {
                  box-sizing: border-box !important;
                  width: 80mm !important;
                  max-width: 80mm !important;
                  padding: 4mm !important;
                }
              }
      ` : "";

      const css = this.collectPrintCss();
      const html = `<!DOCTYPE html>
        <html>
          <head>
            <title>Print Preview</title>
            <base href="${window.location.origin}/">
            <style>${css}</style>
            <style>
              @page {
                size: ${pageSize};
                margin: ${pageMargin};
              }
              html, body {
                margin: 0;
                background: #fff;
              }
              @media screen {
                body { padding: ${pageMargin}; }
              }
              .hm-paper, #invoice, #invoice-a4, #invoice-a5 {
                box-sizing: border-box !important;
                position: static !important;
                left: auto !important;
                top: auto !important;
                width: 100% !important;
                max-width: 100% !important;
                max-height: none !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: visible !important;
                box-shadow: none !important;
              }
              .a4-items, .a4-totals, .a4-top, .a4-head, .signs, .bill-to, .in-word, .inv-banner, .inv-footer-msg {
                max-width: 100%;
                box-sizing: border-box;
              }
              @media print {
                body, body * { visibility: visible !important; }
                body { padding: 0 !important; }
                #invoice, #invoice-a4, #invoice-a5 {
                  position: static !important;
                  width: 100% !important;
                }
              }
              ${thermalCss}
            </style>
          </head>
          <body>
            ${this.printMarkup(source)}
            <script>
              (function () {
                var closed = false;
                var dialogOpened = false;
                function finish() {
                  if (closed) return;
                  closed = true;
                  try {
                    if (window.opener) {
                      window.opener.postMessage({ hmPrintClose: true }, "*");
                    }
                  } catch (err) {}
                  try { window.close(); } catch (err) {}
                }
                window.addEventListener("load", function () {
                  setTimeout(function () {
                    window.onafterprint = finish;
                    window.addEventListener("afterprint", finish);
                    window.addEventListener("focus", function () {
                      // Cancel/Print returns focus to the tab on Firefox/macOS.
                      if (dialogOpened) finish();
                    });
                    try {
                      window.matchMedia("print").addEventListener("change", function (event) {
                        if (event.matches) dialogOpened = true;
                        else if (dialogOpened) finish();
                      });
                    } catch (err) {}

                    window.focus();
                    try { window.print(); } catch (err) {}
                    dialogOpened = true;
                    // Blocking print(): dialog already closed → close tab.
                    // Async print(): afterprint / focus / matchMedia will close it.
                    setTimeout(finish, 400);
                  }, 200);
                });
              })();
            <\/script>
          </body>
        </html>`;
      const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));

      // POS autoprint: load Print Preview in this window (same as Invoice Print → Print).
      if (sameWindow || this.isAutoPrint) {
        window.location.replace(blobUrl);
        return;
      }

      const preview = window.open(blobUrl, "_blank");
      if (!preview) {
        URL.revokeObjectURL(blobUrl);
        window.print();
        return;
      }
      const closePreview = () => {
        window.removeEventListener("message", onPrintMessage);
        URL.revokeObjectURL(blobUrl);
        try { if (!preview.closed) preview.close(); } catch (err) { /* already closed */ }
        if (closeOpenerOnDone) {
          setTimeout(() => {
            try { window.close(); } catch (err) { /* popup may be blocked from closing */ }
          }, 150);
        }
      };
      const onPrintMessage = (event) => {
        if (event.source === preview && event.data && event.data.hmPrintClose) closePreview();
      };
      window.addEventListener("message", onPrintMessage);
      const watchPreview = () => {
        preview.onafterprint = closePreview;
        preview.addEventListener("afterprint", closePreview);
        try {
          let dialogOpened = false;
          preview.matchMedia("print").addEventListener("change", (event) => {
            if (event.matches) dialogOpened = true;
            else if (dialogOpened) closePreview();
          });
        } catch (err) { /* matchMedia unavailable */ }
      };
      preview.addEventListener("load", () => {
        watchPreview();
        // Print dialogs do not always emit afterprint. Closing when the
        // preview window is focused again covers both Print and Cancel.
        setTimeout(() => {
          preview.addEventListener("focus", closePreview);
        }, 800);
      });
    },

    reset() {
      this.q = "";
      this.results = [];
      this.summary = {};
      this.voucher_result = [];
      this.invoiceStyle = null;
    },
  },
};
</script>

<style scoped>
.ip-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 150px);
  min-height: 420px;
  padding: 16px 22px 12px;
  background: #fff;
  overflow: hidden;
}

.ip-card-autoprint {
  height: auto;
  min-height: 0;
  border: 0;
  box-shadow: none;
}

.ip-autoprint-status {
  padding: 8px 0;
  font-size: 0.9rem;
}

.ip-card form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.ip-search {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.ip-label {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #44403c;
  white-space: nowrap;
}

.ip-search-field {
  position: relative;
  flex: 1;
  max-width: 480px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ip-search-field .ip-input {
  flex: 1;
  min-width: 0;
}

.ip-clear {
  flex: 0 0 auto;
  height: 42px;
}

.ip-input {
  height: 42px;
  border-radius: 8px;
}

.ip-results {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 72px;
  top: calc(100% + 4px);
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid #e7e5e4;
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(28, 25, 23, 0.12);
}

.ip-results li {
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.ip-results li.is-active,
.ip-results li:hover {
  background: #1c1917;
  color: #fff;
}

.ip-stage {
  background: #f4f1ec;
  border-radius: 12px;
  padding: 28px 16px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.hm-paper {
  width: min(820px, 100%);
  height: auto;
  max-height: none;
  overflow: visible;
  background: #fff;
  padding: 28px 32px 32px;
  box-shadow: 0 12px 32px rgba(28, 25, 23, 0.12);
  flex: 0 0 auto;
}

.hm-paper-a5 {
  width: min(560px, 100%);
  padding: 10px 12px 12px;
}

.hm-paper-thermal {
  width: 320px;
  padding: 18px 16px 22px;
}

.ip-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f0eeeb;
}

@media print {
  body * {
    visibility: hidden;
  }

  #invoice, #invoice *,
  #invoice-a4, #invoice-a4 *,
  #invoice-a5, #invoice-a5 * {
    visibility: visible;
  }

  #invoice, #invoice-a4, #invoice-a5 {
    position: static !important;
    left: auto !important;
    top: auto !important;
    max-height: none !important;
    overflow: visible !important;
    border: none !important;
    box-shadow: none !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
}
</style>

<style>
.hm-inv {
  color: #1c1917;
}

.hm-inv-thermal {
  font-family: "Courier New", Courier, monospace;
  font-size: 12px;
  line-height: 1.45;
}

.th-center { text-align: center; }
.th-strong { font-weight: 700; }
.th-logo { width: 64px; height: 64px; display: inline-block; }
.th-company { margin-top: 8px; }
.th-banner { margin-top: 8px; white-space: pre-wrap; overflow-wrap: anywhere; }
.th-item { margin-top: 6px; }
.th-word { margin-top: 8px; }
.th-invoice-barcode {
  margin-top: 8px;
  margin-bottom: 4px;
  line-height: 0;
}
.th-invoice-barcode img {
  max-width: 70mm;
  max-height: 50px;
  width: auto;
  height: 50px;
  object-fit: contain;
  display: inline-block;
  vertical-align: middle;
}
.th-powered { margin-top: 10px; font-size: 10px; }
.hm-inv-thermal .co-name { font-size: 14px; }
.th-rule { margin: 5px 0; }
.th-dash { border-top: 1px dashed #1c1917; margin: 6px 0; }
.th-pair { display: flex; justify-content: space-between; gap: 8px; }
.th-head, .th-row { display: flex; }
.th-head .c-sl { width: 10%; }
.th-head .c-item { width: 40%; }
.th-head .c-num, .th-row .c-num { width: 15%; text-align: right; }
.th-head .c-num:last-child, .th-row .c-num:last-child { width: 20%; }
.c-name { width: 50%; font-weight: 500; }

.hm-inv-a4 {
  font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-size: 13px;
  line-height: 1.45;
}

.a4-head {
  padding-bottom: 14px;
  border-bottom: 2px solid #1c1917;
}

.a4-top {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.co-block {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.co-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  flex: 0 0 auto;
}

.inv-banner {
  text-align: center;
  font-weight: 700;
  margin-top: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.inv-footer-msg {
  text-align: center;
  margin: 18px 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.inv-invoice-barcode {
  text-align: center;
  margin: 12px 0 6px;
  line-height: 0;
}
.inv-invoice-barcode img {
  max-width: 220px;
  max-height: 50px;
  width: auto;
  height: 50px;
  object-fit: contain;
  display: inline-block;
  vertical-align: middle;
}

.inv-powered {
  text-align: center;
  margin: 10px 0 0;
  font-size: 11px;
  color: #78716c;
}

.hm-paper-a5 .hm-inv-a4 { font-size: 11px; line-height: 1.3; }
.hm-paper-a5 .co-name { font-size: 15px; }
.hm-paper-a5 .co-logo { width: 44px; height: 44px; }
.hm-paper-a5 .inv-title { font-size: 14px; letter-spacing: 0.1em; margin-bottom: 2px; }
.hm-paper-a5 .a4-totals { width: min(100%, 280px); margin-top: 8px; }
.hm-paper-a5 .a4-head { padding-bottom: 8px; }
.hm-paper-a5 .inv-banner { margin-top: 6px; }
.hm-paper-a5 .bill-to { margin: 8px 0; gap: 4px 10px; }
.hm-paper-a5 .a4-items { margin-top: 8px; }
.hm-paper-a5 .a4-items th,
.hm-paper-a5 .a4-items td { padding: 3px 4px; }
.hm-paper-a5 .a4-totals td { padding: 2px 0; }
.hm-paper-a5 .a4-totals .grand td { padding-top: 4px; }
.hm-paper-a5 .in-word { margin: 8px 0 0; padding: 6px 8px; }
.hm-paper-a5 .inv-footer-msg { margin: 8px 0 0; }
.hm-paper-a5 .inv-invoice-barcode { margin: 6px 0 4px; }
.hm-paper-a5 .inv-invoice-barcode img { max-height: 40px; height: 40px; }
.hm-paper-a5 .inv-powered { margin: 4px 0 0; font-size: 10px; }

.co-name {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.co-line { color: #57534e; margin-top: 2px; }

.a4-meta { text-align: right; color: #44403c; }

.inv-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.16em;
  margin-bottom: 8px;
}

.bill-to {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 6px 12px;
  margin: 16px 0 18px;
  padding: 12px 14px;
  background: #fafaf9;
  border-radius: 8px;
}

.bill-to span:nth-child(odd) { color: #78716c; }

.a4-items {
  width: 100%;
  border-collapse: collapse;
}

.a4-items th {
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #57534e;
  background: #f5f5f4;
  border-bottom: 1px solid #d6d3d1;
  text-align: left;
}

.a4-items td {
  padding: 9px 10px;
  border-bottom: 1px solid #e7e5e4;
}

.a4-items .c, .a4-totals .c { text-align: center; }
.a4-items .r, .a4-totals .r { text-align: right; font-variant-numeric: tabular-nums; }

.a4-items-warranty th,
.a4-items-warranty td {
  font-size: 11px;
  padding: 6px 4px;
}

.hm-paper-warranty .inv-title {
  letter-spacing: 0.08em;
}

.a4-totals {
  width: 360px;
  margin: 14px 0 0 auto;
  border-collapse: collapse;
}

.a4-totals td { padding: 4px 0; }

.a4-totals .grand td {
  padding-top: 8px;
  border-top: 2px solid #1c1917;
  font-weight: 700;
}

.in-word {
  margin: 18px 0 0;
  padding: 10px 12px;
  background: #fafaf9;
  border-left: 3px solid #1c1917;
}

.signs {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 18px;
  margin-top: 36px;
  text-align: center;
  color: #57534e;
  font-size: 12px;
}

.signs span {
  display: block;
  height: 52px;
  border-bottom: 1px solid #a8a29e;
  margin-bottom: 8px;
}
</style>

